import styles from '@Panels/stock-panel/stock-panel.module.scss';
import type {InputFieldProps} from '@Components/input-field-v2';
import {InputField} from '@Components/input-field-v2';
import type {InputFieldControlItemData, InputFieldControlItemEvent} from '@Components/input-field-v2/input-field.types';
import {InputControlItemType, InputFieldSize, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {InputFieldIconID} from '@Panels/stock-panel/stock-panel.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Notification} from '@Components/notification';
import type {ReactElement, Ref} from 'react';
import React, {useRef, useState} from 'react';
import {Button, Size, Type} from '@Components/button';
import {ElementTestId} from '@Tests/tests.types';

interface InputFieldCompleteProps extends InputFieldProps {
  ref: Ref<HTMLInputElement | HTMLTextAreaElement>;
}

export interface StockPanelSearchBarProps {
  numberOfFiltersApplied?: number;
  className?: string;
  placeholderText: string;
  showFilterButton?: boolean;
  isFilterSelected?: boolean;
  isFilterButtonDisabled?: boolean;
  filterButtonText?: string;
  showCrossIcon?: boolean;
  inputFieldStyles?: string;

  onSearch(searchTerm: string): void;

  onFilterClicked?(): void;
  val?: string;
}

export default function StockPanelSearchBar({
  showFilterButton = false,
  isFilterSelected = false,
  onFilterClicked = (): void => {},
  isFilterButtonDisabled = false,
  ...props
}: StockPanelSearchBarProps): ReactElement<any> {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchTerm, setSearchTerm] = useState(props.val ?? '');
  const commonPropsForInputField: InputFieldCompleteProps = {
    isControlled: false,
    inputType: InputFieldType.SEARCH,
    ref: inputRef,
    input: searchTerm,
    onInputChange: (updatedTerm: string): void => {
      setSearchTerm(updatedTerm);
    },
    onInputSubmit: (): void => {
      if (inputRef && inputRef.current) {
        inputRef.current.blur();
      }
      setSearchTerm(searchTerm?.trim());
      props.onSearch(searchTerm?.trim());
    },
  };

  const getControlsForSearch = (): InputFieldControlItemData[] => {
    let controls: Array<InputFieldControlItemData> = [
      {
        id: InputFieldIconID.SEARCH,
        type: InputControlItemType.ICON_BTNS,
        icon: 'icon-search',
      },
    ];

    if (props.showCrossIcon && searchTerm !== '') {
      controls = [
        {
          id: InputFieldIconID.CANCEL,
          type: InputControlItemType.ICONS_ONLY,
          icon: 'icon-close',
        },
        ...controls,
      ];
    }
    return controls;
  };

  const getInputField = (): ReactElement<any> => {
    return (
      <InputField
        {...commonPropsForInputField}
        size={InputFieldSize.MEDIUM}
        className="_unmargin"
        isControlled
        inputClassName={`${styles.searchBar} ${props.inputFieldStyles ?? ''}`}
        placeholder={props.placeholderText}
        onInputChange={(updatedTerm: string): void => {
          setSearchTerm(updatedTerm);
          if (!updatedTerm) {
            props.onSearch('');
          }
        }}
        onInputSubmit={(): void => {
          if (inputRef && inputRef.current) {
            inputRef.current.blur();
          }
          setSearchTerm(searchTerm?.trim());
          if (searchTerm?.trim().length) {
            props.onSearch(searchTerm?.trim());
          }
        }}
        controls={{
          onClick(id: string, e?: InputFieldControlItemEvent): void {
            e?.stopPropagation();
            if ((id as InputFieldIconID) === InputFieldIconID.CANCEL) {
              setSearchTerm('');
              props.onSearch('');
            } else {
              props.onSearch(searchTerm);
            }
          },
          showDefaultControls: false,
          showSingleDefaultControlOnly: false,
          customControls: getControlsForSearch(),
        }}
      />
    );
  };

  return (
    <div className={`${styles.searchbarContainer} flex-h-row flex-items-center ${props.className ?? ''}`} data-testid={ElementTestId.STOCK_PANEL_SEARCH_BAR}>
      {getInputField()}
      {showFilterButton ? (
        <div className={`${styles.filterButtonContainer} flex-center`}>
          {props.filterButtonText ? (
            <Button
              onClick={onFilterClicked}
              disabled={isFilterButtonDisabled}
              size={Size.MEDIUM}
              text={props.filterButtonText}
              icon="icon-filter-2"
              type={isFilterSelected ? Type.PRIMARY : Type.SECONDARY}
            />
          ) : (
            <Icon
              icon="icon-filter-2"
              type={isFilterSelected ? IconType.PRIMARY : IconType.SECONDARY}
              shape={IconShape.SQUARE}
              size={IconSize.SIZE_ICON_24}
              onClick={onFilterClicked}
              isDisabled={isFilterButtonDisabled}
              isUsedAsButton
            />
          )}
          {props.numberOfFiltersApplied ? (
            <div className={styles.notificationDotContainer}>
              <Notification text={`${props.numberOfFiltersApplied}`} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
